import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Radar as RadarIcon, Loader2, ExternalLink, FilePlus2, BookMarked, X, RotateCcw, Settings2, Plus, Trash2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg, fmtDate, fmtRel } from "@/lib/api";
import { useMe } from "@/lib/me";
import { FORMATS, formatLabel, SCOPES, scopeLabel } from "@/lib/constants";
import { PageHeader, Pill, ScopeBadge, EmptyState, SampleTag, SectionTag } from "@/components/app/shared";
import { cn } from "@/lib/utils";

function SourcesSheet({ open, setOpen }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["sources"], queryFn: async () => (await api.get("/radar/sources")).data, enabled: open,
    staleTime: 0, refetchOnWindowFocus: true, refetchInterval: open ? 15_000 : false });
  const [form, setForm] = useState({ name: "", url: "", scope: "LOCAL_SV" });
  const inv = () => qc.invalidateQueries({ queryKey: ["sources"] });
  const add = useMutation({
    mutationFn: async () => (await api.post("/radar/sources", { ...form, active: true })).data,
    onSuccess: () => { inv(); qc.invalidateQueries({ queryKey: ["dashboard"] }); setForm({ name: "", url: "", scope: form.scope }); toast.success("Fuente agregada · todo el equipo la verá en el Radar"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggle = useMutation({ mutationFn: async (s) => api.patch(`/radar/sources/${s.id}`, { name: s.name, url: s.url, scope: s.scope, active: !s.active }), onSuccess: inv });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/radar/sources/${id}`), onSuccess: () => { inv(); toast.success("Fuente eliminada"); } });
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full overflow-y-auto bg-card sm:max-w-xl" data-testid="radar-sources-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-4xl font-normal">Fuentes del Radar</SheetTitle>
          <SheetDescription>Feeds RSS que el Radar lee en cada escaneo. Usa Google News RSS para temas sin feed propio.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 grid gap-2 rounded-lg border border-border bg-surface-2 p-3">
          <Input placeholder="Nombre de la fuente" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="source-name-input" />
          <Input placeholder="https://…/feed" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="font-mono text-xs" data-testid="source-url-input" />
          <div className="flex gap-2">
            <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
              <SelectTrigger className="flex-1" data-testid="source-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SCOPES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => add.mutate()} disabled={!form.name || !form.url || add.isPending} data-testid="source-add-button">
              <Plus className="mr-1 h-4 w-4" /> Agregar
            </Button>
          </div>
        </div>
        {SCOPES.map((sc) => (
          <div key={sc.id} className="mt-6">
            <div className="kicker mb-2">{sc.label}</div>
            <div className="divide-y divide-border rounded-lg border border-border">
              {data.filter((s) => s.scope === sc.id).map((s) => (
                <div key={s.id} className="flex items-center gap-3 px-3 py-2.5" data-testid="source-row">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{s.name}</div>
                    <div className="truncate font-mono text-[10.5px] text-muted-foreground">{s.lastStatus}{s.lastFetchedAt ? ` · ${fmtRel(s.lastFetchedAt)}` : ""}</div>
                    {s.createdByName && <div className="truncate text-[10.5px] text-muted-foreground" data-testid="source-added-by">Agregada por {s.createdByName} · {fmtRel(s.createdAt)}</div>}
                  </div>
                  <Switch checked={s.active} onCheckedChange={() => toggle.mutate(s)} aria-label="Activar fuente" />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del.mutate(s.id)} aria-label="Eliminar fuente">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </SheetContent>
    </Sheet>
  );
}

function PitchCard({ it, highlight, onConvert, onBook, onDiscard, onRestore, busy }) {
  const ref = useRef(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight]);
  return (
    <article ref={ref} data-testid="radar-pitch-card" className={cn("flex flex-col rounded-lg border bg-card p-5 transition-colors", highlight ? "border-primary ring-2 ring-primary" : "border-border")}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <ScopeBadge scope={it.scope} />
          <Pill>{formatLabel(it.format)}</Pill>
          {it.section && <SectionTag section={it.section} />}
          {it.relevanceScore >= 90 && <Pill tone="blush">tendencia</Pill>}
          {it.isSample && <SampleTag />}
        </div>
        <div className="text-right">
          <div className="font-display text-4xl leading-none" data-testid="radar-score">{it.relevanceScore}</div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">relevancia</div>
        </div>
      </div>
      <h3 className="mt-4 text-lg font-semibold leading-snug tracking-tight">{it.suggestedTitle}</h3>
      <div className="mt-3 kicker">Por qué importa ahora</div>
      <p className="mt-1 text-[13.5px] leading-relaxed text-foreground/85">{it.editorialAngle}</p>
      {it.rawTitle && it.rawTitle !== it.suggestedTitle && <p className="mt-3 border-l-2 border-border pl-3 text-xs italic text-muted-foreground">Original: {it.rawTitle}</p>}
      {it.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">{it.tags.map((t) => <span key={t} className="font-mono text-[10.5px] text-muted-foreground">#{t}</span>)}</div>
      )}
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
        <span className="truncate">{it.sourceName} · {fmtDate(it.publishedAt, { day: "2-digit", month: "short", year: "numeric" })}{it.statusByName && it.status !== "NEW" ? ` · ${it.status === "PROMOTED" ? "convertido" : it.status === "BOOK" ? "al Libro" : "descartado"} por ${it.statusByName}` : it.addedByName ? ` · escaneo de ${it.addedByName}` : ""}</span>
        <a href={it.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-1 text-foreground hover:underline" data-testid="radar-source-link">
          Fuente <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {it.status === "NEW" || it.status === "BOOK" ? (
          <>
            <Button size="sm" onClick={onConvert} disabled={busy} data-testid="radar-convert-button">
              <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> Convertir en historia
            </Button>
            {it.status === "NEW" && (
              <Button size="sm" variant="outline" onClick={onBook} disabled={busy} data-testid="radar-flag-book-button">
                <BookMarked className="mr-1.5 h-3.5 w-3.5" /> Marcar para Libro
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onDiscard} disabled={busy} data-testid="radar-discard-button">
              <X className="mr-1 h-3.5 w-3.5" /> Descartar
            </Button>
          </>
        ) : it.status === "PROMOTED" ? (
          <Button size="sm" variant="outline" asChild>
            <Link to={`/editorial/${it.storyId}`} data-testid="radar-open-story">Abrir historia</Link>
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onRestore} data-testid="radar-restore-button">
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restaurar
          </Button>
        )}
      </div>
      {it.status === "ARCHIVED" && it.discardReason && <div className="mt-3 text-xs text-muted-foreground">Motivo: {it.discardReason}</div>}
    </article>
  );
}

export default function Radar() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("NEW");
  const [scope, setScope] = useState("ALL");
  const [format, setFormat] = useState("ALL");
  const [minScore, setMinScore] = useState("0");
  const [scanId, setScanId] = useState(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [discard, setDiscard] = useState(null);
  const [reason, setReason] = useState("");
  const highlight = params.get("item");

  const { data: pub } = useQuery({ queryKey: ["settings-public"], queryFn: async () => (await api.get("/settings/public")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["radar", status],
    queryFn: async () => (await api.get("/radar/items", { params: { status } })).data,
    staleTime: 0, refetchOnWindowFocus: true, refetchInterval: 15_000,
  });
  const { data: scan } = useQuery({
    queryKey: ["scan", scanId],
    queryFn: async () => (await api.get(`/radar/scans/${scanId}`)).data,
    enabled: !!scanId,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2000 : false),
  });
  const { data: latest } = useQuery({ queryKey: ["scan-latest"], queryFn: async () => (await api.get("/radar/scans/latest")).data,
    staleTime: 0, refetchOnWindowFocus: true, refetchInterval: 10_000 });

  useEffect(() => {
    if (latest?.status === "running" && !scanId) setScanId(latest.id);
  }, [latest, scanId]);

  useEffect(() => {
    if (!scan || scan.status === "running") return;
    if (scan.status === "done") toast.success(`Escaneo completado${scan.byName && scan.by !== me.email ? ` (de ${scan.byName})` : ""} · ${scan.created} pitches nuevos`);
    if (scan.status === "error") toast.error(scan.error || "El escaneo falló");
    qc.invalidateQueries({ queryKey: ["radar"] });
    qc.invalidateQueries({ queryKey: ["scan-latest"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    setScanId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan?.status]);

  const startScan = useMutation({
    mutationFn: async () => (await api.post("/radar/scan")).data,
    onSuccess: (job) => setScanId(job.id),
    onError: (e) => toast.error(errMsg(e)),
  });

  useEffect(() => {
    if (params.get("scan") === "1" && pub) {
      params.delete("scan");
      setParams(params, { replace: true });
      if (pub.aiConfigured) startScan.mutate();
      else toast.error("Configura la clave de IA para escanear");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pub]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["radar"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const act = useMutation({
    mutationFn: async ({ id, action, body }) => (await api.post(`/radar/${id}/${action}`, body || {})).data,
    onSuccess: (res, { action }) => {
      refresh();
      if (action === "convert") {
        qc.invalidateQueries({ queryKey: ["stories"] });
        toast.success("Borrador creado en la Mesa Editorial", { action: { label: "Abrir", onClick: () => navigate(`/editorial/${res.id}`) } });
      }
      if (action === "flag-book") toast.success("Agregado al backlog del Libro Anual");
      if (action === "discard") toast.success("Descartado · el Radar evitará temas similares");
      if (action === "restore") toast.success("Pitch restaurado");
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const items = (data?.items || []).filter(
    (i) => (scope === "ALL" || i.scope === scope) && (format === "ALL" || i.format === format) && (i.relevanceScore || 0) >= Number(minScore)
  );
  const running = !!scanId && (scan?.status ?? "running") === "running";

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="02 · Radar Mediana · Descubrimiento de tendencias"
        title="Radar AI"
        subtitle="Lee fuentes de El Salvador, Centroamérica y medios globales; la IA filtra política partidaria, nota roja y PR, y propone pitches con el tono Mediana."
        actions={
          <>
            <Button variant="outline" onClick={() => setSourcesOpen(true)} data-testid="radar-sources-button">
              <Settings2 className="mr-1.5 h-4 w-4" /> Fuentes
            </Button>
            <Button onClick={() => startScan.mutate()} disabled={running || startScan.isPending || !pub?.aiConfigured} data-testid="radar-scan-button">
              {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RadarIcon className="mr-1.5 h-4 w-4" />}
              {running ? "Escaneando…" : "Ejecutar escaneo"}
            </Button>
          </>
        }
      />

      {pub && !pub.aiConfigured && (
        <div className="flex flex-col gap-3 rounded-lg border border-butter-strong bg-butter p-4 text-ink md:flex-row md:items-center md:justify-between" data-testid="radar-ai-missing">
          <div className="flex items-start gap-3">
            <KeyRound className="mt-0.5 h-4 w-4" />
            <div className="text-sm">
              <b>Falta la clave de IA.</b> El escaneo necesita una clave de Gemini, OpenAI o Anthropic.{" "}
              {me.role === "SUPERADMIN" ? "Configúrala en Webflow & Integraciones." : "Pídele a un Superadmin que la configure."}
            </div>
          </div>
          {me.role === "SUPERADMIN" && (
            <Button size="sm" variant="outline" onClick={() => navigate("/integraciones")} data-testid="radar-configure-ai">Configurar IA</Button>
          )}
        </div>
      )}

      {running && (
        <div className="flex items-center gap-3 rounded-lg border border-primary bg-accent p-4" data-testid="radar-scan-progress">
          <Loader2 className="h-4 w-4 animate-spin" />
          <div className="text-sm">
            <b>{scan?.stage || "Iniciando"}</b>
            {scan?.byName && scan.by !== me.email && <span className="ml-2">· escaneo iniciado por {scan.byName}</span>}
            <span className="ml-2 text-muted-foreground">
              {scan?.fetched ? `${scan.fetched} notas leídas · ${scan.candidates} candidatas` : "Leyendo feeds… suele tardar menos de un minuto"}
            </span>
          </div>
        </div>
      )}
      {!running && latest?.finishedAt && (
        <div className="font-mono text-[11px] text-muted-foreground" data-testid="radar-last-scan">
          Último escaneo {fmtRel(latest.finishedAt)}{latest.byName ? ` por ${latest.byName}` : ""} · {latest.status === "error" ? `error: ${latest.error}` : `${latest.created ?? 0} pitches creados`}
        </div>
      )}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            {[["NEW", "Nuevos"], ["BOOK", "Para el Libro"], ["PROMOTED", "Convertidos"], ["ARCHIVED", "Descartados"]].map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`radar-tab-${k}`}>
                {l} <span className="ml-1.5 font-mono text-[10px] opacity-60">{data?.counts?.[k] ?? ""}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-md border border-border p-0.5" data-testid="radar-scope-filter">
            {[{ id: "ALL", label: "Todo" }, ...SCOPES].map((s) => (
              <button key={s.id} onClick={() => setScope(s.id)} data-testid={`radar-scope-${s.id}`}
                className={cn("rounded px-2.5 py-1 text-xs transition-colors", scope === s.id ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>
                {s.label}
              </button>
            ))}
          </div>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger className="h-8 w-[200px]" data-testid="radar-format-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los formatos</SelectItem>
              {FORMATS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={minScore} onValueChange={setMinScore}>
            <SelectTrigger className="h-8 w-[140px]" data-testid="radar-score-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Toda relevancia</SelectItem>
              <SelectItem value="60">60 o más</SelectItem>
              <SelectItem value="80">80 o más</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-72" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState
          testid="radar-empty"
          title={status === "NEW" ? "Sin señales nuevas" : "Nada por aquí"}
          text={status === "NEW" ? "Ejecuta un escaneo para leer las fuentes y generar nuevos pitches." : "Ajusta los filtros o cambia de pestaña."}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((it) => (
            <PitchCard
              key={it.id}
              it={it}
              highlight={highlight === it.id}
              busy={act.isPending}
              onConvert={() => act.mutate({ id: it.id, action: "convert" })}
              onBook={() => act.mutate({ id: it.id, action: "flag-book" })}
              onDiscard={() => { setDiscard(it); setReason(""); }}
              onRestore={() => act.mutate({ id: it.id, action: "restore" })}
            />
          ))}
        </div>
      )}

      <Dialog open={!!discard} onOpenChange={(o) => !o && setDiscard(null)}>
        <DialogContent data-testid="radar-discard-dialog">
          <DialogHeader>
            <DialogTitle>Descartar pitch</DialogTitle>
            <DialogDescription>El motivo entrena el filtro de exclusión: el Radar evitará temas similares en próximos escaneos.</DialogDescription>
          </DialogHeader>
          <div className="text-sm font-medium">{discard?.suggestedTitle}</div>
          <div className="grid gap-1.5">
            <Label>Motivo (opcional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej. demasiado comercial, ya lo cubrimos, política" data-testid="radar-discard-reason" />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {["Política partidaria", "Nota roja", "Comunicado de prensa", "Ya cubierto", "Fuera de tono"].map((r) => (
                <button key={r} onClick={() => setReason(r)} className="rounded-full border border-border px-2 py-0.5 text-[11px] hover:bg-accent">{r}</button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiscard(null)}>Cancelar</Button>
            <Button
              onClick={() => { act.mutate({ id: discard.id, action: "discard", body: { reason } }); setDiscard(null); }}
              data-testid="radar-discard-confirm"
            >
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SourcesSheet open={sourcesOpen} setOpen={setSourcesOpen} />
    </div>
  );
}

export { scopeLabel };
